import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, first, firstValueFrom, from, map} from 'rxjs';
import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {FieldPermission} from '@docma-mm-core/field/field-permission.enum';
import {deepClone} from 'fast-json-patch';
import {MmFaultChangePageService} from '../../../mm-faults-view/mm-fault-right-pane/mm-fault-change-page.service';
import {MmFaultRightPanePageEnum} from '../../../mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.enum';
import {MmDbService} from '../../../../mm-db/mm-db-service';
import {ProjectService} from '@mydocma/project';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {MatLegacySlideToggleChange as MatSlideToggleChange} from '@angular/material/legacy-slide-toggle';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {FieldConfig, FieldConfigDb} from '@docma-mm-core/field-config/field-config';
import {MmFaultFormService} from '../../mm-fault-form.service';
import {FieldDefaultNameEnum} from '@docma-mm-core/field/field-default-name.enum';
import {GetValue, SettingsService} from '../../../../mydocma-settings/settings.service';
import {switchMap} from 'rxjs/operators';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {SettingNamesEnum} from '../../../../mydocma-settings/setting-names.enum';


@UntilDestroy()
@Injectable()
export class FieldConfigurationService implements OnDestroy {

  private fieldsSubject = new BehaviorSubject<FieldConfig[]>(null);
  fields$ = this.fieldsSubject.asObservable();

  private initFieldData: FieldConfig[];

  formGroup: UntypedFormGroup;
  allProjects: boolean;

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  constructor(private readonly projectLoaderService: MmProjectLoaderService,
              private readonly changeService: MmFaultChangePageService,
              private readonly projectService: ProjectService,
              private readonly formBuilder: UntypedFormBuilder,
              private readonly mmDbService: MmDbService,
              private readonly settingsService: SettingsService,
              private readonly personalizationService: PersonalizationService,
              private readonly faultFormService: MmFaultFormService) {

    this.projectLoaderService.fields.pipe(
      map(p => [...p.data
        .filter(x =>
          [FieldDefaultNameEnum.CreatedOn, FieldDefaultNameEnum.ModifiedOn, FieldDefaultNameEnum.Creator, FieldDefaultNameEnum.Modifier].some(f => f ===  x.Prop) === false &&
          ['LastActivitySC_Creator', 'LastActivityCT_Creator', 'AttributeGroup_Image', 'Structure_LongPath'].some(f => f ===  x.Id) === false
        )]),
      untilDestroyed(this)
    ).subscribe(async apiFields => {
      this.loadingSubject.next(true);

      const structField = apiFields.find(p => p.Id === "Structure");

      if (structField) {
        structField.Permission = FieldPermission.Mandatory;
      }

      const fieldsFromDB = await this.loadFieldsFromDb();
      let fieldConfigs: FieldConfig[] = [];

      if(fieldsFromDB) {

        fieldsFromDB.forEach(dbField => {
          const field = apiFields.find(p => p.Id === dbField.Id);
          if(field) {
            fieldConfigs.push({
              Field: field,
              SortId: dbField.SortId,
              Visibility: dbField.Visibility,
              Memory: dbField.Memory,
            });
            apiFields.splice(apiFields.indexOf(field), 1);
          }
        });
        let maxSortId = Math.max(...fieldConfigs.map(f => f.SortId));

        apiFields.forEach((p, index)=> {
          fieldConfigs.push({
            Field: p,
            Memory: false,
            Visibility: true,
            SortId: (maxSortId + index) + 1
          });
        })

      } else {

        const fieldGroups = this.faultFormService.getFaultFieldGroups(apiFields);
        const fields = [...fieldGroups.descriptionFields.concat(fieldGroups.executionFields, fieldGroups.responsibleFields, fieldGroups.classificationFields, fieldGroups.projectFields, fieldGroups.costFields)];
        let index = 0;

        const test = apiFields.filter(x => fields.findIndex(y => y.Id == x.Id) === -1);

        for (const field of fields) {
          const found = apiFields.find(x => x.Id === field.Id);
          if (found) {
            fieldConfigs.push({
              Field: found,
              Memory: false,
              Visibility: true,
              SortId: index + 1
            })
          }
          else {
            console.log('')
          }
        }
        // fieldConfigs = apiFields.map((f, index) => ({
        //   Field: f,
        //   Memory: false,
        //   Visibility: true,
        //   SortId: index + 1
        // }));
      }

      fieldConfigs.filter(field => field.Field.Permission === FieldPermission.Mandatory)
                  .forEach(field => field.Visibility = true);

      fieldConfigs.filter(field => field.Field.Permission === FieldPermission.Read)
                  .forEach(field => field.Memory = false);


      fieldConfigs = fieldConfigs.sort((x,y) => (y.Visibility ? 1 : 0) - (x.Visibility ? 1 : 0)
                                                || x.SortId - y.SortId);

      fieldConfigs.filter(field => field.Field.Id === 'Number2').forEach(p  => p.MemoryDeactivated = true);


      this.initFieldData = deepClone(fieldConfigs);
      this.fieldsSubject.next(fieldConfigs);

      this.loadingSubject.next(false);
    });

    this.formGroup = this.formBuilder.group({
      allProjects: [this.allProjects]
    });
  }

  public notifyChanges(changedFields: FieldConfig[]): void {
    if(this.detectChanges(changedFields))  {
      this.changeService.setChanges(MmFaultRightPanePageEnum.fieldConfiguration);
    } else {
      this.changeService.disableChanges(MmFaultRightPanePageEnum.fieldConfiguration);
    }
  }

  public revertChanges(): void {
    this.fieldsSubject.next(this.initFieldData);
    this.changeService.disableChanges(MmFaultRightPanePageEnum.fieldConfiguration);
  }

  public async saveChanges(allFields: FieldConfig[]): Promise<void> {
    const project = await firstValueFrom(this.projectService.currentProject.pipe(first()));

    allFields.forEach( p => p.Memory = p.Visibility && p.Memory);

    const saved = allFields.map(p => ({
      Id: p.Field.Id,
      Visibility: p.Visibility,
      Memory: p.Memory,
      SortId: p.SortId
    }) as FieldConfigDb);

    if(this.allProjects) {
      await this.removeProjectSettings();
      await this.settingsService.save(JSON.stringify(saved), PersonalizationKeysEnum.FieldConfig, SettingNamesEnum.FieldConfig, null);
    } else {

      await firstValueFrom(this.projectService.currentProject.pipe(
        switchMap(project => this.settingsService.save(JSON.stringify(saved), PersonalizationKeysEnum.FieldConfig, SettingNamesEnum.FieldConfig, project))
      ));
    }

    this.fieldsSubject.next(allFields);
    this.initFieldData = deepClone(allFields);
  }

  private async removeProjectSettings(): Promise<void> {
    const tableNames = await (await (await this.mmDbService.getDb()).db).getAllKeys("personalization" as never);
    // console.log("Table names " , tableNames);

    await Promise.all(tableNames.filter(name => name.toString().includes('.FieldConfig'))
      .map(async name => {
        await this.personalizationService.deleteValue(name.toString());
      }));

    await firstValueFrom(this.projectService.currentProject.pipe(switchMap(project => this.settingsService.deleteAll(SettingNamesEnum.FieldConfig, project))));
  }

  private async loadFieldsFromDb(): Promise<FieldConfigDb[]> {
    var projectSetting = await firstValueFrom(this.projectService.currentProject.pipe(
      switchMap(project => from(this.settingsService.load(PersonalizationKeysEnum.FieldConfig, SettingNamesEnum.FieldConfig, project))
      )
    ));

    const projectSettingValue = await GetValue<FieldConfigDb[]>(projectSetting);
    if (projectSettingValue) {
      return projectSettingValue;
    }

    var setting = await firstValueFrom(from(this.settingsService.load(PersonalizationKeysEnum.FieldConfig, SettingNamesEnum.FieldConfig, null)));
    const settingValue = await GetValue<FieldConfigDb[]>(setting);
    if (settingValue) {
      return settingValue;
    }

    return null;
  }

  private detectChanges(newFields: FieldConfig[]): boolean {

    const sortedFields = [...newFields]
      .sort((x,y) => (y.Visibility ? 1 : 0) - (x.Visibility ? 1 : 0)
                 || x.SortId - y.SortId);

    for(let i = 0; i < sortedFields.length; i++) {
      if(!this.compareField(sortedFields[i], this.initFieldData[i])) {
        return true;
      }
    }

    return false;
  }

  private compareField(oldField: FieldConfig, newField:FieldConfig): boolean {
    return oldField.Visibility === false && newField.Visibility == false ||
           (oldField.Field.Id === newField.Field.Id &&
            oldField.Memory === newField.Memory &&
            oldField.Visibility === newField.Visibility);
  }

  ngOnDestroy(): void {
  }

  public onToggleChanged(event: MatSlideToggleChange): void {
    if (event.checked !== this.allProjects) {
      this.allProjects = event.checked;
    }
  }

}
